import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import {
  generateShadow,
  H6,
  IUserContext,
  SectionSeparator,
  Subtitle1,
  useAlert,
  userContext,
  useTheme,
} from "capsule"
import dayjs from "dayjs"
import React, { FC, useContext, useMemo } from "react"
import { Trans, useTranslation } from "react-i18next"
import { StyleSheet, Text, View } from "react-native"

import { collections } from "../../common/types"
import SubmitButton from "../../components/SubmitButton"
import useItemI18n from "../../features/hooks/useItemI18n"
import { HomeNS } from "../../features/i18n/constants"
import { AppUserData } from "../../features/models/UserData"
import { updateUserData } from "../../features/models/UserDataFunctions"
import { IMainParamList } from "../MainScreen/MainNavigator"

interface Props {
  shouldShow: boolean
}

const HomeSurgeryDateConfirmView: FC<Props> = ({ shouldShow }) => {
  const { t } = useTranslation(HomeNS)
  const navigation = useNavigation<StackNavigationProp<IMainParamList>>()
  const { userData, userDocRef } = useContext<IUserContext<AppUserData>>(userContext)
  const { showDialog } = useAlert()

  const {
    fontMaker,
    dimensions: { spacing },
    colors: {
      white: { highEmphasis: white },
      surface: { background },
      primary,
    },
  } = useTheme()

  const s = useMemo(
    () => ({
      boldText: [fontMaker({ weight: "Bold" })],
      container: [
        styles.container,
        {
          backgroundColor: background,
        },
      ],
      ratingContainer: [
        styles.ratingContainer,
        {
          backgroundColor: white,
          margin: spacing,
        },
        generateShadow(2),
      ],
      contentContainer: {
        padding: spacing,
      },
      textContainer: {
        alignItems: "center" as const,
      },
      title: {
        textAlign: "center" as const,
        marginBottom: spacing,
      },
      surgery: {
        textAlign: "center" as const,
        marginBottom: spacing / 2,
      },
      date: {
        textAlign: "center" as const,
        marginBottom: spacing,
      },
      buttonContainer: [
        styles.buttonContainer,
        {
          marginTop: spacing / 2,
        },
      ],
    }),
    [spacing, white, background, fontMaker],
  )

  const onModify = () => {
    updateUserData(userDocRef, {
      surgeryDateConfirmed: true,
    })
    navigation.navigate("OperationEdition")
  }

  const onConfirm = () => {
    showDialog({
      type: "button",
      title: t("surgeryConfirmation.title"),
      message: t("surgeryConfirmation.message"),
      dismissable: false,
      positive: {
        label: t("surgeryConfirmation.understood"),
        onPress: () => {
          updateUserData(userDocRef, {
            surgeryDateConfirmed: true,
          })
        },
        textStyle: { color: primary },
      },
    })
  }

  const pathologyName = useItemI18n(collections.PATHOLOGIES, userData?.pathology as string, "name")
  const surgeryDate = dayjs(userData?.surgeryDate).format("DD MMM YYYY")

  if (!shouldShow) {
    return null
  }

  return (
    <View style={s.container}>
      <SectionSeparator />
      <View style={s.ratingContainer}>
        <View style={s.contentContainer}>
          <View style={s.textContainer}>
            <H6 style={s.title}>{t("surgeryTitle")}</H6>
            <Subtitle1 style={[s.surgery, s.boldText]}>{pathologyName}</Subtitle1>
            <Subtitle1 style={s.date}>
              <Trans
                i18nKey={`${HomeNS}:surgeryDateIs`}
                values={{ date: surgeryDate }}
                components={{
                  p: <></>,
                  b: <Text style={s.boldText} />,
                }}
              />
            </Subtitle1>
          </View>
          <View style={s.buttonContainer}>
            <SubmitButton
              label={t("modifySurgeryDate")}
              viewStyle={styles.bottomButton}
              onSubmit={onModify}
            />
            <SubmitButton
              label={t("confirmSurgeryDate")}
              viewStyle={styles.bottomButton}
              onSubmit={onConfirm}
            />
          </View>
        </View>
      </View>
      <SectionSeparator />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  ratingContainer: {
    borderRadius: 20,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  bottomButton: {
    width: "40%",
  },
})

export default HomeSurgeryDateConfirmView
